import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Link2,
  Wallet,
  Building2,
  CreditCard,
  Users,
  ChevronUp,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper component for profile avatar with fallback
const ProfileAvatar = ({ person, size = 'md', className = '' }) => {
  const [imageError, setImageError] = useState(false);
  
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'w-20 h-20',
  };
  
  const textSizes = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-4xl',
  };

  const imageUrl = person?.profile_image_url 
    ? (person.profile_image_url.startsWith('/api') 
        ? `${BACKEND_URL}${person.profile_image_url}` 
        : person.profile_image_url)
    : null;

  if (!imageUrl || imageError) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-white/20 flex items-center justify-center ${className}`}>
        <span className={`font-semibold ${size === 'lg' ? 'text-white font-serif' : 'text-primary-800'} ${textSizes[size]}`}>
          {person?.full_name?.charAt(0).toUpperCase() || '?'}
        </span>
      </div>
    );
  }

  return (
    <img 
      src={imageUrl}
      alt={person?.full_name || 'Profile'}
      className={`${sizeClasses[size]} rounded-full object-cover ${className}`}
      onError={() => setImageError(true)}
    />
  );
};

const MarketerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchMarketerDetails();
  }, [id]);

  const fetchMarketerDetails = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getMarketerDetails(id);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load marketer details');
      navigate('/admin/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const { marketer, upline, downlines } = data || {};

  return (
    <div className="min-h-screen bg-neutral-background" data-testid="marketer-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-neutral-border px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="rounded-lg"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </Button>
          <h1 className="font-heading text-2xl font-semibold text-primary-900">Marketer Details</h1>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 lg:p-8 space-y-8">
        {/* Profile Header */}
        <Card className="border-neutral-border shadow-card overflow-hidden" data-testid="profile-header-card">
          <div className="bg-gradient-to-r from-primary-900 to-primary-700 p-8">
            <div className="flex items-center gap-6">
              <ProfileAvatar person={marketer} size="lg" className="border-4 border-white/30" />
              <div className="text-white">
                <h2 className="text-2xl font-semibold">{marketer?.full_name}</h2>
                <p className="text-primary-200">{marketer?.email}</p>
                <Badge className="mt-2 bg-white/20 text-white border-0">
                  {marketer?.recruits_count || 0} Recruits
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Personal Information */}
          <Card className="border-neutral-border shadow-card" data-testid="personal-info-card">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-primary-900">Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-neutral-text-muted">Full Name</p>
                  <p className="font-medium text-primary-900">{marketer?.full_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-neutral-text-muted">Email</p>
                  <p className="font-medium text-primary-900">{marketer?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-neutral-text-muted">Phone / WhatsApp</p>
                  <p className="font-medium text-primary-900">{marketer?.phone}</p>
                  <p className="text-sm text-neutral-text-muted">WhatsApp: {marketer?.whatsapp}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-neutral-text-muted">Location</p>
                  <p className="font-medium text-primary-900">{marketer?.location}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-accent-50">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                  <Link2 className="h-5 w-5 text-accent-600" />
                </div>
                <div>
                  <p className="text-xs text-neutral-text-muted">Referral Code</p>
                  <p className="font-mono font-bold text-accent-600">{marketer?.referral_code}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Information (Admin Only) */}
          <Card className="border-neutral-border shadow-card border-l-4 border-l-accent-500" data-testid="bank-info-card">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-primary-900 flex items-center gap-2">
                <Wallet className="h-5 w-5 text-accent-600" />
                Bank Details
              </CardTitle>
              <CardDescription>Confidential - Admin Only</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-accent-600" />
                </div>
                <div>
                  <p className="text-xs text-neutral-text-muted">Bank Name</p>
                  <p className="font-medium text-primary-900">{marketer?.bank_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-accent-600" />
                </div>
                <div>
                  <p className="text-xs text-neutral-text-muted">Account Number</p>
                  <p className="font-mono font-medium text-primary-900">{marketer?.bank_account_number}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-accent-600" />
                </div>
                <div>
                  <p className="text-xs text-neutral-text-muted">Account Holder Name</p>
                  <p className="font-medium text-primary-900">{marketer?.bank_account_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Hierarchy Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upline (Recruiter) */}
          <Card className="border-neutral-border shadow-card" data-testid="upline-card">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-primary-900 flex items-center gap-2">
                <ChevronUp className="h-5 w-5 text-primary-600" />
                Upline (Recruited By)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upline ? (
                <Link
                  to={`/admin/marketers/${upline.id}`}
                  className="flex items-center justify-between p-4 rounded-xl bg-primary-50 hover:bg-primary-100 transition-colors group"
                  data-testid="upline-link"
                >
                  <div className="flex items-center gap-4">
                    <ProfileAvatar person={upline} size="md" className="border-2 border-primary-200" />
                    <div>
                      <p className="font-medium text-primary-900">{upline.full_name}</p>
                      <p className="text-sm text-neutral-text-muted">{upline.email}</p>
                      <p className="text-xs text-neutral-400">{upline.location}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-primary-400 group-hover:text-primary-600" />
                </Link>
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                    <Users className="h-6 w-6 text-neutral-400" />
                  </div>
                  <p className="text-neutral-text-muted">No upline - Direct registration</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Downlines (Recruits) */}
          <Card className="border-neutral-border shadow-card" data-testid="downlines-card">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-primary-900 flex items-center gap-2">
                <ChevronDown className="h-5 w-5 text-accent-600" />
                Downlines ({downlines?.length || 0} Recruits)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {downlines && downlines.length > 0 ? (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {downlines.map((downline, index) => (
                    <Link
                      key={downline.id}
                      to={`/admin/marketers/${downline.id}`}
                      className="flex items-center justify-between p-3 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors group"
                      data-testid={`downline-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <ProfileAvatar person={downline} size="sm" className="border-2 border-accent-200" />
                        <div>
                          <p className="font-medium text-primary-900 text-sm">{downline.full_name}</p>
                          <p className="text-xs text-neutral-text-muted">{downline.location}</p>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-neutral-400 group-hover:text-primary-600" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                    <Users className="h-6 w-6 text-neutral-400" />
                  </div>
                  <p className="text-neutral-text-muted">No recruits yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MarketerDetail;
